import React from "react";

import CheckCircleOutlinedMD from "@busbud/design-system-components/dist/Icons/CheckCircleOutlined/MD";
import InfoMD from "@busbud/design-system-components/dist/Icons/Info/MD";
import WarningMD from "@busbud/design-system-components/dist/Icons/Warning/MD";

import { getPurchaseExpiredPoliciesByType } from "@app/helpers/purchase/get-purchase-refund-policies";
import { generateHepstarRefundLink } from "@app/modules/account/components/trip-details/cancel-status/helpers/hepstar-cart-item";
import type { HepstarBannerChip } from "@app/modules/account/components/trip-details/hepstar-banner";
import { SubmitClaimButton } from "@app/modules/account/components/trip-details/transaction-actions/submit-claim-button";
import type { TranslatorService } from "@app/services/translator";
import { helpUrl } from "@app/services/url-builders/company-pages-url-builder";
import type { NapiPurchaseCancellation } from "@app/types/napi-v2-cancellation-types";
import type { NapiV2Purchase } from "@app/types/napi-v2-purchase-types";
import type { CreditType } from "@app/types/napi-v2-request-type";
import { redirect } from "@app/utils/window";

import type { CancelDialog } from "../cancel-dialog";
import type { RefundFormState } from "../cancel-trip";
import type { SupportedLocale } from "@config/locales";

export const getTitle = (
  status: RefundFormState,
  translator: TranslatorService
): string | undefined => {
  switch (status) {
    case "not_refundable_anymore":
    case "error":
      return translator.t("!account.trip-details.cancellation-error.title");
    case "already_cancelled":
      return translator.t(
        "!account.trip-details.cancellation-already-cancelled.title"
      );
    case "pending":
      return translator.t("!account.trip-details.cancellation-pending.title2");
    case "success":
      return translator.t("!account.trip-details.cancellation-success.title2");
    case "denied":
      return translator.t(
        "!account.trip-details.cancel-tickets.cancellation-denied.title"
      );
  }
};

export const getIcon = (status: RefundFormState): React.ReactNode => {
  const common_props = { width: 20, height: 20 };
  switch (status) {
    case "not_refundable_anymore":
    case "error":
      return <WarningMD {...common_props} color="palette.orange.regular.500" />;
    case "pending":
      return <InfoMD {...common_props} color="palette.blue.regular.500" />;
    case "success":
      return (
        <CheckCircleOutlinedMD
          {...common_props}
          color="palette.green.regular.500"
        />
      );
  }
};

export const getMessage = (
  status: RefundFormState,
  purchase: NapiV2Purchase,
  translator: TranslatorService,
  refund_method: CreditType | undefined,
  purchase_cancellation?: NapiPurchaseCancellation
): string | undefined => {
  switch (status) {
    case "error":
      return translator.t("!account.trip-details.cancellation-error.message");
    case "pending":
      return translator.t("!account.trip-details.cancellation-pending.message");
    case "success":
      return getSuccessMessage(
        translator,
        refund_method,
        purchase_cancellation
      );
    case "already_cancelled":
      return translator.t("!accounts.my-tickets.cancel.modal.cancelled", {
        purchase_number: purchase.id
      });
    case "not_refundable_anymore": {
      const { expired_purchase_date_policy, expired_departure_date_policy } =
        getPurchaseExpiredPoliciesByType(purchase);

      if (!!expired_purchase_date_policy) {
        return translator.t(
          "!purchasepage.purchase.no_longer_refundable_after_purchase",
          { cutoff_time: expired_purchase_date_policy.cutoff_to }
        );
      }

      return translator.t(
        "!purchasepage.purchase.no_longer_refundable_before_departure",
        { cutoff_time: expired_departure_date_policy?.cutoff_to || "" }
      );
    }
  }
};

const getSuccessMessage = (
  translator: TranslatorService,
  refund_method: CreditType | undefined,
  purchase_cancellation?: NapiPurchaseCancellation
): string => {
  const voucher_with_reference =
    purchase_cancellation?.cancellation_voucher_references?.[0] || null;

  if (voucher_with_reference) {
    return `${translator.t(
      "!account.trip-details.cancellation-success.message2"
    )}
    <br>
    <br>
    ${translator.t(
      "!account.trip-details.cancellation-success.voucher.message1"
    )}
    <br>
    <h2>${voucher_with_reference.reference}</h2>
    <br>
    ${translator.t(
      "!account.trip-details.cancellation-success.voucher.message2"
    )}
    <br>
    <br>
    ${translator.t(
      "!account.trip-details.cancellation-success.voucher.message3"
    )}`;
  }

  if (refund_method === "voucher") {
    return translator.t(
      "!account.trip-details.cancellation-success.voucher.message3"
    );
  }

  // If reimbursed with Busbud credits
  if (
    purchase_cancellation &&
    purchase_cancellation.credited_amount.amount > 0
  ) {
    return translator.t(
      "!account.trip-details.cancellation-success.busbud-credits.message"
    );
  }

  return `${translator.t(
    "!account.trip-details.cancellation-success.message2"
  )}<br><br>${translator.t(
    "!account.trip-details.cancellation-success.message3"
  )}`;
};

export const getButtons = (
  status: RefundFormState,
  translator: TranslatorService
): Partial<React.ComponentProps<typeof CancelDialog>> => {
  switch (status) {
    case "error":
    case "denied":
    case "not_refundable_anymore":
      return {
        primary_button_config: {
          label: translator.t("!purchase-confirmation.help.contact-us")
        },
        secondary_button_config: {
          label: translator.t("!modal.cta.close")
        }
      };
    case "success":
    case "pending":
    case "already_cancelled":
    default:
      return {
        secondary_button_config: {
          label: translator.t("!modal.cta.close")
        }
      };
  }
};

export const getCtaAction = (
  status: RefundFormState,
  locale: SupportedLocale
) => {
  switch (status) {
    case "error":
    case "not_refundable_anymore":
      redirect(helpUrl(locale));
      break;
    default:
      return;
  }
};

type HepstarBannerChipProps = Partial<
  React.ComponentProps<typeof HepstarBannerChip>
>;

export const getHepstarBannerProps = (
  status: RefundFormState,
  purchase: NapiV2Purchase,
  translator: TranslatorService
): HepstarBannerChipProps => {
  const props: HepstarBannerChipProps = {
    title: translator.t("!ancillary.hepstar-banner.title")
  };

  const message_body = translator.t(
    "!ancillary.hepstar-banner.after-operator-cancel.body"
  );

  const alt_message_body = translator.t(
    "!ancillary.hepstar-banner.support-evidence.body"
  );

  const refund_link = generateHepstarRefundLink(purchase);
  switch (status) {
    case "success":
      props.direction = "column";
      props.footer = <SubmitClaimButton link={refund_link} />;
      props.title = translator.t(
        "!ancillary.hepstar-banner.complete-refund.title"
      );
      props.message = `${translator.t(
        "!ancillary.hepstar-banner.full-refund.body"
      )} ${message_body}`;
      break;
    case "not_refundable_anymore":
    case "already_cancelled":
    case "pending":
    case "denied":
    case "error":
      props.footer = <SubmitClaimButton link={refund_link} />;
      props.title = translator.t("!ancillary.hepstar-banner.use-refund.title");
      props.message = `${translator.t(
        "!ancillary.hepstar-banner.full-refund.body2",
        {
          refund_amount: "100%"
        }
      )} ${alt_message_body}`;
      break;
    default:
      props.message = `${translator.t(
        "!ancillary.hepstar-banner.after-operator-cancel.body"
      )} ${message_body}`;
  }

  return props;
};
